import { SystemMessages } from 'utils/enums/system-message.enum'
import {
  addCustomIssue,
  emailFieldSchema,
  nameFieldSchema,
  realDateFieldSchema,
  realNameFieldSchema,
  selectIdFieldSchema,
} from 'utils/schema.util'
import {
  doNotHasText,
  doNotNullOrEmpty,
  isFilled,
  validateDocuments,
  validateInfractions,
  validatePhones,
  validateSocialNetworks,
} from 'utils/validate.util'
import { z } from 'zod'
import { personAddressInit, personAddressSchema } from './address.type'
import { documentInit, documentSchema } from './document.type'
import { infractionInit, infractionSchema } from './infraction.type'
import { phoneInit, phoneSchema } from './phone.type'
import { socialNetworkInit, socialNetworkSchema } from './social-network.type'
import { booleanSimNao } from 'utils/text.util'
import dayjs from 'dayjs'

const LIVING_WITH_OTHER_OPTION_ID = '38'
const DO_NOT_KEEP_IN_TOUCH_OPTION_ID = 'N'
const WELCOMED_REASON_OTHER_OPTION_ID = '10'

export type Substances =
  | 'usaCigarro'
  | 'usaBebida'
  | 'usaMaconha'
  | 'usaCocaina'
  | 'usaCrack'
  | 'usaHaxixe'
  | 'usaHeroina'
  | 'usaLolo'
  | 'usaOutros'

type SubstancesType = { name: Substances; label: string }

export const substances: SubstancesType[] = [
  { name: 'usaCigarro', label: 'Cigarro' },
  { name: 'usaBebida', label: 'Bebida Alcoólica' },
  { name: 'usaMaconha', label: 'Maconha' },
  { name: 'usaCocaina', label: 'Cocaína' },
  { name: 'usaCrack', label: 'Crack' },
  { name: 'usaHaxixe', label: 'Haxixe' },
  { name: 'usaHeroina', label: 'Heroína' },
  { name: 'usaLolo', label: 'Loló' },
  { name: 'usaOutros', label: 'Outros' },
]

export const teenagerPictureSchema = z.object({
  id: z.number().nullish(),
  arquivoBase64: z.string().nullish(),
  arquivoNome: z.string().nullish(),
  arquivoTipo: z.string().nullish(),
})

export type TeenagerPictureType = z.infer<typeof teenagerPictureSchema>

export const teenagerPictureInit: TeenagerPictureType = {}

export const teenagerIdentificationSchema = z
  .object({
    acolhidoInsticionalmente: z.string().nullish(),
    acolhidoInsticionalmenteEstadoRJ: z.string().nullish(),
    antecedenteInfracional: z.string().nullish(),
    atoInfracionais: z
      .array(infractionSchema)
      .transform((fields) => {
        return validateInfractions(fields)
      })
      .nullish(),
    descricaoAtividadeRemunerada: z.string().nullish(),
    descricaoAtividadeResponsavel: z.string().nullish(),
    descricaoGuardaFilho: z.string().nullish(),
    descricaoIdRendaFamiliar: z.string().nullish(),
    descricaoIdResidenciaResponsavel: z.string().nullish(),
    descricaoMotivoAcolhidoInstitucionalmente: z.string().nullish(),
    descricaoIdAcolhidoInsticionalmenteMunicipioEstadoRJ: z.string().nullish(),
    descricaoIdAcolhidoInsticionalmenteServicoAcolhimento: z.string().nullish(),
    descricaoIdAcolhidoInsticionalmenteOutroEstado: z.string().nullish(),
    descricaoMotivoEvasao: z.string().nullish(),
    descricaoTempoEvasao: z.string().nullish(),
    descricaoValorRemuneracao: z.string().nullish(),
    descricaoidMotivoAcolhidoInstitucionalmente: z.string().nullish(),
    estaEstudando: z.string().nullish(),
    anoConclusaoEstudo: z.number().nullish(),
    exerceAtividadeRemunerada: z.string().nullish(),
    generoOutro: z.string().nullish(),
    idMotivoAcolhidoInstitucionalmente: selectIdFieldSchema,
    idMotivoEvasao: selectIdFieldSchema,
    idRendaFamiliar: selectIdFieldSchema,
    idResidenciaResponsavel: selectIdFieldSchema,
    idTempoEvasao: selectIdFieldSchema,
    idValorRemuneracao: selectIdFieldSchema,
    idAcolhidoInsticionalmenteMunicipioEstadoRJ: selectIdFieldSchema,
    idAcolhidoInsticionalmenteServicoAcolhimento: selectIdFieldSchema,
    idAcolhidoInsticionalmenteOutroEstado: selectIdFieldSchema,
    idade: z.number().nullish(),
    mantemContatoMae: selectIdFieldSchema,
    motivoNaoMantemContatoMae: z.string().nullish(),
    mantemContatoPai: selectIdFieldSchema,
    motivoNaoMantemContatoPai: z.string().nullish(),
    nomeInstituicaoEnsino: z.string().nullish(),
    possuiFilho: z.string().nullish(),
    possuiIrmao: z.string().nullish(),
    estaGestante: z.string().nullish(),
    procuradoConselhoTutelar: z.string().nullish(),
    procuradoEscola: z.string().nullish(),
    quantidadeFilho: z.number().nullish(),
    quantidadeIrmao: z.number().nullish(),
    recebePensaoAlimenticia: selectIdFieldSchema,
    responsavelTrabalha: z.string().nullish(),
    situacaoRua: z.string().nullish(),
    tratamentoMental: z.string().nullish(),
    usaBebida: z.string().nullish(),
    usaCigarro: z.string().nullish(),
    usaCocaina: z.string().nullish(),
    usaCrack: z.string().nullish(),
    usaHaxixe: z.string().nullish(),
    usaHeroina: z.string().nullish(),
    usaLolo: z.string().nullish(),
    usaMaconha: z.string().nullish(),
    usaOutros: z.string().nullish(),
    usaSubstancia: z.string().nullish(),
    nomeResponsavelResidenciaOutro: realNameFieldSchema.nullish(),
    tipoTratamentoMental: z.string().nullish(),
    instituicaoTratamentoMental: selectIdFieldSchema.nullish(),
    inseridoJovemAprendiz: z.string().nullish(),
    dataInicioJovemAprendiz: z.string().nullish(),
    empresaJovemAprendiz: z.string().nullish(),
    contatoJovemAprendiz: z.string().nullish(),
    turnoJovemAprendiz: z.string().nullish(),
    oportunidadesJovemAprendizEcaminhadas: z.string().nullish(),
    motivoNaoAdesaoJovemAprendiz: selectIdFieldSchema.nullish(),
    motivoNaoInseridoJovemAprendiz: selectIdFieldSchema.nullish(),
    apresentaAreaInteresseJovemAprendiz: z.string().nullish(),
    objetivoAreaInteresseJovemAprendiz: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string) => addCustomIssue(ctx, [field], SystemMessages.MESSAGE_002)

    if (
      fields.idResidenciaResponsavel === LIVING_WITH_OTHER_OPTION_ID &&
      doNotHasText(fields.nomeResponsavelResidenciaOutro)
    ) {
      addIssue('nomeResponsavelResidenciaOutro')
    }

    if (fields.estaEstudando === 'N' && doNotHasText(fields.idTempoEvasao)) {
      addIssue('idTempoEvasao')
    }

    if (fields.estaEstudando === 'N' && doNotHasText(fields.idMotivoEvasao)) {
      addIssue('idMotivoEvasao')
    }

    if (
      doNotNullOrEmpty(fields.exerceAtividadeRemunerada) &&
      booleanSimNao(fields.exerceAtividadeRemunerada) &&
      doNotHasText(fields.descricaoAtividadeRemunerada)
    ) {
      addIssue('descricaoAtividadeRemunerada')
    }

    if (
      doNotNullOrEmpty(fields.exerceAtividadeRemunerada) &&
      booleanSimNao(fields.exerceAtividadeRemunerada) &&
      doNotHasText(fields.idValorRemuneracao)
    ) {
      addIssue('idValorRemuneracao')
    }

    if (
      fields.mantemContatoMae === DO_NOT_KEEP_IN_TOUCH_OPTION_ID &&
      doNotHasText(fields.motivoNaoMantemContatoMae)
    ) {
      addIssue('motivoNaoMantemContatoMae')
    }

    if (
      fields.mantemContatoPai === DO_NOT_KEEP_IN_TOUCH_OPTION_ID &&
      doNotHasText(fields.motivoNaoMantemContatoPai)
    ) {
      addIssue('motivoNaoMantemContatoPai')
    }

    if (
      doNotNullOrEmpty(fields.possuiFilho) &&
      booleanSimNao(fields.possuiFilho) &&
      doNotHasText(fields.quantidadeFilho)
    ) {
      addIssue('quantidadeFilho')
    }

    if (
      doNotNullOrEmpty(fields.possuiFilho) &&
      booleanSimNao(fields.possuiFilho) &&
      doNotHasText(fields.descricaoGuardaFilho)
    ) {
      addIssue('descricaoGuardaFilho')
    }

    if (
      doNotNullOrEmpty(fields.possuiIrmao) &&
      booleanSimNao(fields.possuiIrmao) &&
      doNotHasText(fields.quantidadeIrmao)
    ) {
      addIssue('quantidadeIrmao')
    }

    if (
      doNotNullOrEmpty(fields.tratamentoMental) &&
      booleanSimNao(fields.tratamentoMental) &&
      doNotHasText(fields.tipoTratamentoMental)
    ) {
      addIssue('tipoTratamentoMental')
    }

    if (
      doNotNullOrEmpty(fields.tratamentoMental) &&
      booleanSimNao(fields.tratamentoMental) &&
      doNotHasText(fields.instituicaoTratamentoMental)
    ) {
      addIssue('instituicaoTratamentoMental')
    }

    if (
      booleanSimNao(fields.acolhidoInsticionalmente) &&
      doNotHasText(fields.idMotivoAcolhidoInstitucionalmente)
    ) {
      addIssue('idMotivoAcolhidoInstitucionalmente')
    }

    if (
      booleanSimNao(fields.acolhidoInsticionalmente) &&
      fields.idMotivoAcolhidoInstitucionalmente === WELCOMED_REASON_OTHER_OPTION_ID &&
      doNotHasText(fields.descricaoMotivoAcolhidoInstitucionalmente)
    ) {
      addIssue('descricaoMotivoAcolhidoInstitucionalmente')
    }

    if (
      booleanSimNao(fields.acolhidoInsticionalmente) &&
      booleanSimNao(fields.acolhidoInsticionalmenteEstadoRJ) &&
      doNotHasText(fields.idAcolhidoInsticionalmenteMunicipioEstadoRJ)
    ) {
      addIssue('idAcolhidoInsticionalmenteMunicipioEstadoRJ')
    }

    if (
      booleanSimNao(fields.acolhidoInsticionalmente) &&
      doNotHasText(fields.acolhidoInsticionalmenteEstadoRJ)
    ) {
      addIssue('acolhidoInsticionalmenteEstadoRJ')
    }

    if (
      booleanSimNao(fields.acolhidoInsticionalmente) &&
      !booleanSimNao(fields.acolhidoInsticionalmenteEstadoRJ) &&
      doNotHasText(fields.idAcolhidoInsticionalmenteOutroEstado)
    ) {
      addIssue('idAcolhidoInsticionalmenteOutroEstado')
    }

    if (booleanSimNao(fields.antecedenteInfracional)) {
      fields.atoInfracionais?.forEach((infraction, index) => {
        const artigoIsEmpty = !isFilled(infraction.artigo)
        const tipoIdIsEmpty = !isFilled(infraction.tipo?.id)

        if (artigoIsEmpty) {
          // Validação para outros campos preenchidos, mas artigo vazio
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SystemMessages.MESSAGE_002,
            path: ['atoInfracionais', index, 'artigo'], // Caminho onde o erro será reportado
          })
        }

        if (tipoIdIsEmpty) {
          // Validação para outros campos preenchidos, mas tipoReferencia.id vazio
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SystemMessages.MESSAGE_002,
            path: ['atoInfracionais', index, 'tipo', 'id'], // Caminho onde o erro será reportado
          })
        }
      })
    }
  })

export const hearingIdentificationSchema = z
  .object({
    foto: teenagerPictureSchema.nullish(),
    idPessoaFisica: z.number().nullish(),
    dataNascimento: realDateFieldSchema.nullish(),
    descricaoIdEstadoNaturalidade: z.string().nullish(),
    descricaoIdGenero: z.string().nullish(),
    descricaoIdGrauEscolaridade: z.string().nullish(),
    descricaoIdMunicipioNaturalidade: z.string().nullish(),
    descricaoIdRaca: z.string().nullish(),
    descricaoIdTipoDocumento: z.string().nullish(),
    documentos: z
      .array(documentSchema)
      .transform((fields) => {
        return validateDocuments(fields)
      })
      .nullish(),
    email: emailFieldSchema.nullish(),
    enderecos: z.array(personAddressSchema).nullish(),
    idEstadoNaturalidade: selectIdFieldSchema.nullish(),
    idGenero: selectIdFieldSchema.nullish(),
    idGrauEscolaridade: selectIdFieldSchema.nullish(),
    idMunicipioNaturalidade: selectIdFieldSchema.nullish(),
    idRaca: selectIdFieldSchema.nullish(),
    idTipoDocumento: selectIdFieldSchema.nullish(),
    identificacaoAdolescente: teenagerIdentificationSchema,
    nomeSocial: realNameFieldSchema.nullish(),
    nome: nameFieldSchema.nullish(),
    nomeMae: realNameFieldSchema.nullish(),
    nomePai: realNameFieldSchema.nullish(),
    nomeResponsavelAdolescente: z.string().nullish(),
    idade: z.string().nullish(),
    telefones: z
      .array(phoneSchema)
      .transform((fields) => {
        return validatePhones(fields)
      })
      .nullish(),
    redesSociais: z
      .array(socialNetworkSchema)
      .transform((fields) => {
        return validateSocialNetworks(fields)
      })
      .nullish(),
    ultimaAtualizacao: z.string().nullish(),
  })
  .superRefine((pessoa, ctx) => {
    if (isFilled(pessoa.dataNascimento)) {
      const diffYears = dayjs(dayjs()).diff(pessoa.dataNascimento, 'year')
      if (diffYears < 12) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Data de nascimento informada não pode ser inferior há 12 anos.',
          path: ['dataNascimento'],
        })
      }
    }

    const tipoCount: Record<string, number[]> = {}
    pessoa.documentos?.forEach((doc, index) => {
      if (tipoCount[doc.idTipoDocumento]) {
        tipoCount[doc.idTipoDocumento].push(index)
      } else {
        tipoCount[doc.idTipoDocumento] = [index]
      }
    })

    Object.values(tipoCount).forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach((indice) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Tipo do documento já informado.',
            path: ['documentos', indice, 'idTipoDocumento'],
          })
          return z.NEVER
        })
      }
    })
  })

export type TeenagerIdentificationType = z.infer<typeof teenagerIdentificationSchema>

export type HearingIdentificationType = z.infer<typeof hearingIdentificationSchema>

export const teenagerIdentificationInit: TeenagerIdentificationType = {
  acolhidoInsticionalmente: '',
  acolhidoInsticionalmenteEstadoRJ: '',
  antecedenteInfracional: '',
  atoInfracionais: [infractionInit],
  descricaoAtividadeRemunerada: '',
  descricaoAtividadeResponsavel: '',
  descricaoGuardaFilho: '',
  descricaoIdRendaFamiliar: '',
  descricaoIdResidenciaResponsavel: '',
  descricaoMotivoAcolhidoInstitucionalmente: '',
  descricaoMotivoEvasao: '',
  descricaoTempoEvasao: '',
  descricaoValorRemuneracao: '',
  descricaoidMotivoAcolhidoInstitucionalmente: '',
  descricaoIdAcolhidoInsticionalmenteMunicipioEstadoRJ: '',
  descricaoIdAcolhidoInsticionalmenteServicoAcolhimento: '',
  descricaoIdAcolhidoInsticionalmenteOutroEstado: '',
  estaEstudando: '',
  anoConclusaoEstudo: null,
  exerceAtividadeRemunerada: '',
  generoOutro: '',
  idMotivoAcolhidoInstitucionalmente: '0',
  idMotivoEvasao: '0',
  idRendaFamiliar: '0',
  idResidenciaResponsavel: '0',
  idTempoEvasao: '0',
  idValorRemuneracao: '0',
  idAcolhidoInsticionalmenteMunicipioEstadoRJ: '0',
  idAcolhidoInsticionalmenteServicoAcolhimento: '0',
  idAcolhidoInsticionalmenteOutroEstado: '0',
  idade: null,
  mantemContatoMae: '0',
  motivoNaoMantemContatoMae: '',
  mantemContatoPai: '0',
  motivoNaoMantemContatoPai: '',
  nomeInstituicaoEnsino: '',
  nomeResponsavelResidenciaOutro: '',
  possuiFilho: '',
  estaGestante: '',
  possuiIrmao: '',
  procuradoConselhoTutelar: '',
  procuradoEscola: '',
  quantidadeFilho: null,
  quantidadeIrmao: null,
  recebePensaoAlimenticia: '',
  responsavelTrabalha: '',
  situacaoRua: '',
  tratamentoMental: '',
  usaBebida: 'N',
  usaCigarro: 'N',
  usaCocaina: 'N',
  usaCrack: 'N',
  usaHaxixe: 'N',
  usaHeroina: 'N',
  usaLolo: 'N',
  usaMaconha: 'N',
  usaOutros: 'N',
  usaSubstancia: '',
  tipoTratamentoMental: '',
  instituicaoTratamentoMental: '',
  inseridoJovemAprendiz: '',
  dataInicioJovemAprendiz: '',
  empresaJovemAprendiz: '',
  contatoJovemAprendiz: '',
  turnoJovemAprendiz: '',
  oportunidadesJovemAprendizEcaminhadas: '',
  motivoNaoAdesaoJovemAprendiz: '',
  motivoNaoInseridoJovemAprendiz: '',
  apresentaAreaInteresseJovemAprendiz: '',
  objetivoAreaInteresseJovemAprendiz: '',
}

export const hearingIdentificationInit: HearingIdentificationType = {
  dataNascimento: '',
  descricaoIdEstadoNaturalidade: '',
  descricaoIdGenero: '',
  descricaoIdGrauEscolaridade: '',
  descricaoIdMunicipioNaturalidade: '',
  descricaoIdRaca: '',
  descricaoIdTipoDocumento: '',
  documentos: [documentInit],
  email: '',
  enderecos: [personAddressInit],
  idEstadoNaturalidade: '0',
  idGenero: '0',
  idGrauEscolaridade: '0',
  idMunicipioNaturalidade: '0',
  idRaca: '0',
  idTipoDocumento: '0',
  identificacaoAdolescente: teenagerIdentificationInit,
  nomeSocial: '',
  nome: '',
  nomeMae: '',
  nomePai: '',
  nomeResponsavelAdolescente: '',
  telefones: [phoneInit],
  redesSociais: [socialNetworkInit],
}
